import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { readSession } from './token';
import { AuthError, type AuthMethod, type AuthProvider, type AuthState, type SignInInput } from './types';

const AuthContext = createContext<AuthProvider | undefined>(undefined);

export function useAuth(): AuthProvider {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('useAuth must be used inside an auth provider.');
  return auth;
}

/** Lets a test or a host supply any AuthProvider implementation. */
export function AuthContextProvider({ value, children }: { value: AuthProvider; children: ReactNode }) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const DEVELOPMENT_STORAGE_KEY = 'care-platform.web.dev-token';
const PASSWORD_STORAGE_KEY = 'care-platform.web.session';

function readStored(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, token: string | null): void {
  try {
    if (token) window.sessionStorage.setItem(key, token);
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage blocked: the session lasts until the page is reloaded.
  }
}

function initialState(token: string | null | undefined): AuthState {
  if (!token) return { status: 'signedOut' };
  try {
    return { status: 'signedIn', session: readSession(token) };
  } catch {
    return { status: 'signedOut', reason: 'expired' };
  }
}

/**
 * A bearer-token session kept for the browser tab (sessionStorage), shared by the development and placeholder
 * providers. `obtain` turns the sign-in input into a token, or throws AuthError.
 */
function useTabSession(
  method: AuthMethod,
  storageKey: string,
  initialToken: string | null | undefined,
  obtain: (input: SignInInput) => Promise<string>,
): AuthProvider {
  const [state, setState] = useState<AuthState>(() => initialState(readStored(storageKey) ?? initialToken));

  const signIn = useCallback(
    async (input?: SignInInput) => {
      if (!input) return;
      const session = readSession(await obtain(input));
      writeStored(storageKey, session.accessToken);
      setState({ status: 'signedIn', session });
    },
    [obtain, storageKey],
  );

  const signOut = useCallback(async () => {
    writeStored(storageKey, null);
    setState({ status: 'signedOut' });
    return Promise.resolve();
  }, [storageKey]);

  const sessionRejected = useCallback(() => {
    writeStored(storageKey, null);
    setState({ status: 'signedOut', reason: 'rejected' });
  }, [storageKey]);

  const getAccessToken = useCallback(async () => {
    if (state.status !== 'signedIn') return null;
    const { expiresAt, accessToken } = state.session;
    if (expiresAt !== null && expiresAt <= Date.now()) {
      writeStored(storageKey, null);
      setState({ status: 'signedOut', reason: 'expired' });
      return null;
    }
    return Promise.resolve(accessToken);
  }, [state, storageKey]);

  return useMemo<AuthProvider>(
    () => ({ method, state, getAccessToken, signIn, signOut, sessionRejected }),
    [method, state, getAccessToken, signIn, signOut, sessionRejected],
  );
}

const pastedToken = (input: SignInInput): Promise<string> =>
  'token' in input
    ? Promise.resolve(input.token)
    : Promise.reject(new AuthError('A token is needed.', 'malformed'));

/**
 * Local development only. Takes a token issued by service-kit's development issuer, either from configuration
 * (`initialToken`, read from `.env.local`) or pasted on the sign-in screen and kept for the browser tab. The token
 * is checked for shape and expiry only; the BFF verifies it.
 */
export function DevelopmentAuthProvider({
  initialToken,
  children,
}: {
  initialToken?: string | null;
  children: ReactNode;
}) {
  const value = useTabSession('development', DEVELOPMENT_STORAGE_KEY, initialToken, pastedToken);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * PLACEHOLDER FOR P1, Development only: email and password, exchanged for a development-issuer token by the BFF's
 * placeholder endpoint (`POST {tokenUrl}`, apps/web-bff/src/dev-identity). Replaced by the identity provider's
 * own AuthProvider when it is chosen; nothing outside this package changes.
 */
export function PasswordAuthProvider({ tokenUrl, children }: { tokenUrl: string; children: ReactNode }) {
  const obtain = useCallback(
    async (input: SignInInput): Promise<string> => {
      if (!('email' in input)) throw new AuthError('An email and password are needed.', 'invalidCredentials');
      let response: Response;
      try {
        response = await fetch(
          new Request(new URL(tokenUrl, window.location.origin), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ email: input.email, password: input.password }),
          }),
        );
      } catch {
        throw new AuthError('Sign-in could not be reached.', 'unavailable');
      }
      if (response.status === 401 || response.status === 400) {
        throw new AuthError('The email or password is incorrect.', 'invalidCredentials');
      }
      if (response.status === 429) throw new AuthError('Too many attempts.', 'throttled');
      if (!response.ok) throw new AuthError('Sign-in is not available.', 'unavailable');
      const body = (await response.json().catch(() => null)) as { accessToken?: unknown } | null;
      if (typeof body?.accessToken !== 'string')
        throw new AuthError('Sign-in is not available.', 'unavailable');
      return body.accessToken;
    },
    [tokenUrl],
  );
  const value = useTabSession('password', PASSWORD_STORAGE_KEY, null, obtain);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** A build with no sign-in method configured. Nothing can be called; the shell says so. */
export function UnconfiguredAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthProvider>(
    () => ({
      method: 'none',
      state: { status: 'unconfigured' },
      getAccessToken: () => Promise.resolve(null),
      signIn: () => Promise.resolve(),
      signOut: () => Promise.resolve(),
      sessionRejected: () => undefined,
    }),
    [],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
